import { useCallback, useState } from "react";
import type { QuarantineReprocessResult, QuarantineRow } from "@rua-watch/shared";
import { useAuthedRawFetch, useMe, useQuarantine, useReprocessQuarantine } from "../api/hooks";
import { fromUnix } from "../lib/format";

// Compact list of failed-ingest entries. The original table only showed
// timestamp/reason/detail/r2 key, which is too thin to triage — every
// row was indistinguishable. The metadata captured at quarantine time
// (sender, subject, filename, size) is now persisted (migration 0005)
// and surfaced here, with click-to-expand for the full detail and a raw
// download for forensic inspection.

export function QuarantineRoute() {
  const { data, isLoading, error } = useQuarantine();
  const [expanded, setExpanded] = useState<number | null>(null);
  const rawFetch = useAuthedRawFetch();
  const me = useMe();
  const canAdmin =
    !!me.data && (me.data.isPlatformAdmin || me.data.roles.includes("admin"));
  const reprocess = useReprocessQuarantine();
  const [lastResult, setLastResult] = useState<QuarantineReprocessResult | null>(null);

  const parseFailedCount = (data ?? []).filter((q) => q.reason === "parse_failed").length;
  const runReprocess = useCallback(
    (input: { ids?: number[] } = {}) => {
      setLastResult(null);
      reprocess.mutate(input, { onSuccess: (r) => setLastResult(r) });
    },
    [reprocess],
  );

  // Raw quarantine downloads need the Authorization header — same trick
  // as /api/reports/:id/raw downloads on the Domain page.
  const downloadRaw = useCallback(
    async (id: number, suggested: string) => {
      const res = await rawFetch(`/api/quarantine/${id}/raw`);
      if (!res.ok) {
        alert(`Download failed: ${res.status} ${res.statusText}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = suggested;
      a.click();
      URL.revokeObjectURL(url);
    },
    [rawFetch],
  );

  return (
    <div className= "space-y-4">
      <div>
        <h1 className= "text-xl font-semibold text-slate-900 dark:text-slate-100">Quarantine</h1>
        <p className= "text-sm text-slate-500 dark:text-slate-400">
          Reports that failed ingestion. Click a row to see the full error detail
          and the originating R2 key. Raw payloads are preserved in R2 — use the
          download button for forensic inspection.
        </p>
        {canAdmin ? (
          <div className= "mt-3 flex flex-wrap items-center gap-3">
            <button
              type= "button"
              disabled={reprocess.isPending || parseFailedCount === 0}
              onClick={() => runReprocess()}
              className= "rounded border border-slate-300 dark:border-slate-700 px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {reprocess.isPending
                ? "Reprocessing…"
                : `Reprocess parse failures${parseFailedCount ? ` (${parseFailedCount})` : ""}`}
            </button>
            {lastResult ? (
              <span className= "text-xs text-slate-600 dark:text-slate-400">
                {lastResult.ingested} ingested · {lastResult.duplicate} duplicate ·{" "}
                {lastResult.stillFailing} still failing · {lastResult.missing} missing
                {lastResult.errors.length ? ` · ${lastResult.errors.length} errors` : ""}
              </span>
            ) : null}
            {reprocess.error ? (
              <span className= "text-xs text-rose-600 dark:text-rose-400">
                {reprocess.error.message}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {isLoading ? <p className= "text-slate-500 dark:text-slate-400">Loading…</p> : null}
      {error ? <p className= "text-red-700 dark:text-red-300">Failed to load.</p> : null}

      {data ? (
        <div className= "overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className= "flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-4 py-2">
            <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Entries</h2>
            <span className= "text-xs text-slate-500 dark:text-slate-400">{data.length}</span>
          </div>
          {data.length === 0 ? (
            <p className= "px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">Empty.</p>
          ) : (
            <table className= "min-w-full text-sm">
              <thead className= "bg-slate-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
                <tr>
                  <th className= "px-4 py-2 font-medium w-8"></th>
                  <th className= "px-4 py-2 font-medium">Received</th>
                  <th className= "px-4 py-2 font-medium">Reason</th>
                  <th className= "px-4 py-2 font-medium">Sender</th>
                  <th className= "px-4 py-2 font-medium">Filename</th>
                  <th className= "px-4 py-2 font-medium text-right">Size</th>
                  <th className= "px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody className= "divide-y divide-slate-100 dark:divide-slate-800">
                {data.map((q) => (
                  <Row
                    key={q.id}
                    q={q}
                    expanded={expanded === q.id}
                    onToggle={() => setExpanded(expanded === q.id ? null : q.id)}
                    onDownload={() =>
                      downloadRaw(
                        q.id,
                        q.filename || q.r2Key.split("/").pop() ||`quarantine-${q.id}.bin`,
                      )
                    }
                    onReprocess={
                      canAdmin && q.reason === "parse_failed"
                        ? () => runReprocess({ ids: [q.id] })
                        : undefined
                    }
                    reprocessing={reprocess.isPending}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Row({
  q,
  expanded,
  onToggle,
  onDownload,
  onReprocess,
  reprocessing,
}: {
  q: QuarantineRow;
  expanded: boolean;
  onToggle: () => void;
  onDownload: () => void;
  onReprocess?: () => void;
  reprocessing?: boolean;
}) {
  return (
    <>
      <tr className= "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60" onClick={onToggle}>
        <td className= "px-4 py-2 text-slate-400 dark:text-slate-500 text-xs">{expanded ?"▾" :"▸"}</td>
        <td className= "px-4 py-2 text-slate-600 dark:text-slate-400 whitespace-nowrap">
          {fromUnix(q.receivedAt)}
        </td>
        <td className= "px-4 py-2">
          <ReasonPill reason={q.reason} />
        </td>
        <td className= "px-4 py-2">
          {q.senderFrom ? (
            <span className= "font-mono text-xs text-slate-700 dark:text-slate-300">{q.senderFrom}</span>
          ) : (
            <span className= "text-xs text-slate-400 dark:text-slate-500">—</span>
          )}
        </td>
        <td className= "px-4 py-2">
          {q.filename ? (
            <span className= "font-mono text-xs text-slate-700 dark:text-slate-300 truncate block max-w-[20rem]">
              {q.filename}
            </span>
          ) : (
            <span className= "text-xs text-slate-400 dark:text-slate-500">—</span>
          )}
        </td>
        <td className= "px-4 py-2 text-right tabular-nums text-xs text-slate-600 dark:text-slate-400">
          {q.sizeBytes != null ? formatBytes(q.sizeBytes) :"—"}
        </td>
        <td className= "px-4 py-2 text-right whitespace-nowrap">
          {onReprocess ? (
            <button
              disabled={reprocessing}
              onClick={(e) => {
                e.stopPropagation();
                onReprocess();
              }}
              className= "mr-2 rounded border border-slate-300 dark:border-slate-700 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              Reprocess
            </button>
          ) : null}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
            className= "rounded border border-slate-300 dark:border-slate-700 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Raw
          </button>
        </td>
      </tr>
      {expanded ? (
        <tr className= "bg-slate-50 dark:bg-slate-800/60">
          <td colSpan={7} className= "px-4 py-3">
            <dl className= "grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-[8rem_1fr]">
              <DetailRow label= "Subject" value={q.subject} />
              <DetailRow label= "Error detail" value={q.errorDetail} />
              <DetailRow label= "R2 key " value={q.r2Key} mono />
            </dl>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | undefined;
  mono?: boolean;
}) {
  return (
    <>
      <dt className= "text-slate-500 dark:text-slate-400 sm:text-right">{label}</dt>
      <dd className={mono ?"font-mono text-slate-700 dark:text-slate-300 break-all" :"text-slate-700 dark:text-slate-300 break-words"}>
        {value && value.length > 0 ? value : <span className= "text-slate-400 dark:text-slate-500">—</span>}
      </dd>
    </>
  );
}

// Reason → severity mapping. parse_failed and no_valid_attachment are
// usually fixable provider quirks (worth investigating). mime_parse_failed
// and r2_object_missing are infrastructure-level red flags — escalate.
function ReasonPill({ reason }: { reason: string }) {
  const severity =
    reason === "mime_parse_failed" || reason === "r2_object_missing"
      ?"red"
      :"amber";
  const color =
    severity === "red"
      ?"bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
      :"bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300";
  return (
    <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${color}`}>
      {reason}
    </span>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
